import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeletePaymentIntentsMutation,
    usePaymentIntentsInfiniteQuery,
} from './usePaymentIntentsQuery';
import {usePaymentIntentsSearch} from './usePaymentIntentsSearch';
import {usePaymentIntentsTableColumns} from './usePaymentIntentsTableColumns';
import {PaymentIntentsFilters} from './usePaymentIntentsFilter';
import {PaymentIntent} from '../types';

/**
 * Default sort: NEWEST FIRST, on `createdAtMillis`.
 *
 * The key must be immutable and non-null. `created` is set by Stripe when the
 * intent is created and never moves. It is nullable on the schema, so rows
 * without one sort LAST in both directions under the shared comparator —
 * correct, since "never recorded" is a state rather than an old date.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<PaymentIntent> = {
    columnAccessor: 'createdAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against.
 *
 * ⚠️ `clientSecret` IS DELIBERATELY NOT MATCHED. It is the bearer credential
 * that authorises confirming this payment intent from the browser. It is
 * selected by the list query (the checkout flow needs it), but making it
 * searchable would turn the search box into an oracle: type a fragment, see
 * which row survives. A value not matched cannot be probed (§11).
 *
 * `amount` is not matched either — it is a number in minor units, so typing
 * "9.00" would match nothing while "900" would match unexpectedly. The Amount
 * column is sortable, which is the right affordance for a numeric field.
 */
const matchesKeyword = (item: PaymentIntent, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.customerId,
        item.description,
        item.status,
        item.currency,
        item.paymentMethod,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter.
 *
 * ⚠️ EVERY filter here is CLIENT-SIDE, over the pages loaded so far.
 * `PaymentIntentsFilterInput` declares only `customerId`, so `status` — the one
 * thing the drawer offers — cannot be pushed to the server. Sending it anyway
 * would be silently ignored by AppSync and the list would come back unfiltered
 * while the badge said otherwise. See usePaymentIntentsFilter.ts.
 */
const matchesFilters = (
    item: PaymentIntent,
    filters: PaymentIntentsFilters,
): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof PaymentIntent];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ═══ MONEY PATH ═══
 *
 * This module creates and confirms PaymentIntents. The scope of this change is
 * the LIST/TABLE layer only: no confirm/capture/cancel semantics, no
 * idempotency key, and no `clientSecret` handling is altered. The delete path
 * still runs through the same service call behind the same confirmation modal.
 *
 * ⚠️ SCOPE OF SORT, KEYWORD AND FILTER: the pages FETCHED SO FAR.
 *
 * The API takes no search term, no sort argument, and a filter input with a
 * single `customerId` field — so unlike payment-methods, NOTHING the drawer
 * offers can be narrowed server-side. Sorting page 1 does not sort the dataset.
 *
 * That is a property of the token-pagination model, not a bug — and it is why
 * the footer states a LOADED count and never a total.
 */
export const usePaymentIntents = (
    customerId?: string,
    filters: PaymentIntentsFilters = {},
) => {
    const query = usePaymentIntentsInfiniteQuery(customerId, filters);
    const {columns} = usePaymentIntentsTableColumns();
    const {keyword} = usePaymentIntentsSearch();
    const deleteMutation = useDeletePaymentIntentsMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: PaymentIntent[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // Normalise Stripe's epoch-SECONDS into millis ONCE, here, so the
        // Created column can both render and sort the same value.
        //
        // ⚠️ FNP-1106. `formatTimestamp` wraps moment, which reads a bare
        // number as MILLISECONDS. Passing `created` (seconds) straight through
        // renders every row as January 1970. `amount` is deliberately NOT
        // touched: it stays a raw minor-unit NUMBER so it sorts numerically.
        .map(item => ({
            ...item,
            createdAtMillis: typeof item.created === 'number'
                ? item.created * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<PaymentIntent>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched` is
    // rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
